import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Container, TrustChips, ProgressBar, InlineLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Seo from "@/components/Seo";
import ShareKit from "@/components/ShareKit";
import ActivityTicker from "@/components/ActivityTicker";
import InstallNudge from "@/components/InstallNudge";
import PushToggle from "@/components/PushToggle";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Heart, Flag, Users, Calendar, Target, MessageSquare, ShieldCheck, AlertTriangle, Clock, Trophy,
  TrendingUp, CheckCircle2, Flame, Bitcoin, Share2, RefreshCw, Crown, BadgeCheck, PiggyBank, Bell, Check, X, HandCoins,
} from "lucide-react";
import confetti from "canvas-confetti";
import WordsOfSupport from "@/components/WordsOfSupport";
import SupporterLeaderboard from "@/components/SupporterLeaderboard";

export default function CampaignDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [cryptoEnabled, setCryptoEnabled] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setC(null);
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("src") || params.get("utm_source") || document.referrer || "";
    try { sessionStorage.setItem("ff_ref", ref); } catch (_) {}
    api.get(`/campaigns/${slug}`, { params: ref ? { ref } : {} })
      .then((r) => { setC(r.data); setFollowing(!!r.data.is_following); setFollowers(r.data.followers_count || 0); })
      .catch((e) => setError(errMsg(e)));
    api.get("/settings/public").then((r) => setCryptoEnabled(r.data.crypto_enabled !== false)).catch(() => {});
  }, [slug]);

  // Milestone celebration: fire confetti once when the campaign crosses 25/50/75/100% funded.
  useEffect(() => {
    if (!c || !c.goal_amount || c.goal_amount <= 0) return;
    const pct = Math.floor((c.amount_raised / c.goal_amount) * 100);
    const milestone = [100, 75, 50, 25].find((m) => pct >= m);
    if (!milestone) return;
    const key = `ff_ms_${c.slug}`;
    let seen = 0;
    try { seen = Number(localStorage.getItem(key) || 0); } catch (_) {}
    if (milestone > seen) {
      try { localStorage.setItem(key, String(milestone)); } catch (_) {}
      const t = setTimeout(() => {
        confetti({ particleCount: 140, spread: 78, origin: { y: 0.7 }, colors: ["#143257", "#C1272D", "#1E9E62"] });
        toast.success(milestone >= 100 ? "🎉 Goal reached! Share the win to keep momentum going." : `🎉 ${milestone}% funded! Share to push it further.`);
      }, 900);
      return () => clearTimeout(t);
    }
  }, [c]);

  const toggleFollow = async () => {
    if (!user) { navigate("/sign-in", { state: { from: `/fundraisers/${slug}` } }); return; }
    try {
      if (following) {
        const { data } = await api.delete(`/campaigns/${c.id}/follow`);
        setFollowing(false); setFollowers(data.followers_count);
      } else {
        const { data } = await api.post(`/campaigns/${c.id}/follow`);
        setFollowing(true); setFollowers(data.followers_count);
        toast.success("You're following this campaign — we'll email you when the organizer posts an update.");
      }
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (error) return <Container className="py-20"><EmptyState icon={AlertTriangle} title="Campaign unavailable" message={error} action={<Button asChild className="bg-ff-red text-white"><Link to="/browse">Browse campaigns</Link></Button>} /></Container>;
  if (!c) return <InlineLoader label="Loading campaign" />;

  const feePct = c.organizer_pro ? 3 : 5;
  const showVideoCover = c.use_video_cover && c.video_status === "approved" && c.video_url;

  return (
    <div className="bg-white">
      <Seo title={c.title} description={(c.story || "").slice(0, 155)}
        image={`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${c.slug}/og-image.png`}
        url={`${window.location.origin}/fundraisers/${c.slug}`} />
      <Container className="grid grid-cols-1 gap-10 py-10 lg:grid-cols-12">
        {/* Main */}
        <div className="lg:col-span-8 min-w-0">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-ff-red">
            <span className="rounded-full bg-[#F3F4F6] px-3 py-1 text-ff-blue">{c.category}</span>
          </div>
          <h1 data-testid="campaign-title" className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-ff-blue sm:text-4xl">
            {c.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ff-medium">
            by <span className="font-semibold text-ff-dark">{c.organizer_name}</span>
            {c.organizer_verified_all && (
              <span data-testid="organizer-verified-badge"
                title="Phone verified · Email verified · Payout account connected"
                className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2.5 py-0.5 text-xs font-bold text-ff-green">
                <BadgeCheck className="h-3.5 w-3.5" /> Verified organizer
              </span>
            )}
          </div>

          {c.organizer_pro && (
            <div className="relative mt-4 flex items-start gap-3 overflow-hidden rounded-xl bg-ff-blue p-4 pl-5 text-white shadow-sm" data-testid="campaign-pro-section">
              <span className="absolute inset-y-0 left-0 w-1.5 bg-ff-action" aria-hidden="true" />
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ff-red text-white"><Crown className="h-5 w-5" /></span>
              <div>
                <div className="flex items-center gap-2 font-heading text-sm font-bold text-white">
                  <Brand /> Pro organizer
                  <span data-testid="campaign-pro-badge" className="inline-flex items-center gap-1 rounded-full bg-ff-red px-2 py-0.5 text-[10px] font-extrabold text-white"><Crown className="h-2.5 w-2.5" /> PRO</span>
                </div>
                <div className="mt-0.5 text-xs text-white/70">Verified Pro tools, custom tax-deductible receipts and priority review.</div>
              </div>
            </div>
          )}

          {showVideoCover ? (
            <div className="mt-5 overflow-hidden rounded-2xl border border-ff-border bg-black">
              <video src={c.video_url} controls poster={c.cover_image || undefined}
                className="max-h-[520px] w-full bg-black" data-testid="campaign-cover-video" />
            </div>
          ) : (
            <div className="mt-5 overflow-hidden rounded-2xl border border-ff-border">
              {c.cover_image && <img src={c.cover_image} alt={c.title} className="max-h-[520px] w-full object-cover" />}
            </div>
          )}

          {/* Photo gallery */}
          {c.gallery?.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="campaign-gallery">
              {c.gallery.map((url, i) => (
                <button key={i} type="button" onClick={() => setLightbox(url)} data-testid={`gallery-thumb-${i}`}
                  className="group overflow-hidden rounded-lg border border-ff-border">
                  <img src={url} alt={`${c.title} photo ${i + 1}`}
                    className="aspect-square w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                </button>
              ))}
            </div>
          )}

          {/* Approved video shown below when the cover is a photo */}
          {!showVideoCover && c.video_url && c.video_status === "approved" && (
            <div className="mt-4 overflow-hidden rounded-2xl border border-ff-border bg-black">
              <video src={c.video_url} controls className="max-h-[520px] w-full bg-black" data-testid="campaign-video" />
            </div>
          )}

          <div className="mt-5"><TrustChips labels={c.trust_labels} /></div>

          <div className="mt-4"><ActivityTicker supporters={c.supporters} donorsLast24h={c.donors_last_24h} /></div>

          <Tabs defaultValue="story" className="mt-8">
            <div className="-mx-1 overflow-x-auto px-1">
              <TabsList className="bg-[#F3F4F6] w-max min-w-full justify-start">
                <TabsTrigger value="story" data-testid="tab-story">Story</TabsTrigger>
                <TabsTrigger value="updates" data-testid="tab-updates">Updates ({c.updates?.length || 0})</TabsTrigger>
                <TabsTrigger value="supporters" data-testid="tab-supporters">Supporters ({c.supporters?.length || 0})</TabsTrigger>
                <TabsTrigger value="words" data-testid="tab-words">Words of Support</TabsTrigger>
                {c.is_team && <TabsTrigger value="team" data-testid="tab-team">Team ({c.team_members?.length || 0})</TabsTrigger>}
              </TabsList>
            </div>

            <TabsContent value="story" className="pt-6">
              <p className="whitespace-pre-line text-base leading-relaxed text-ff-dark">{c.story}</p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <InfoRow icon={Users} label="Beneficiary" value={c.beneficiary_name || "—"} />
                <InfoRow icon={Heart} label="Relationship" value={c.relationship || "—"} />
                <InfoRow icon={Target} label="Intended use" value={c.intended_use || "—"} />
                <InfoRow icon={Calendar} label="Goal" value={c.goal_amount ? money(c.goal_amount) : "No fixed goal"} />
              </div>
            </TabsContent>

            <TabsContent value="updates" className="pt-6">
              {c.updates?.length ? (
                <div className="space-y-5">
                  {c.updates.map((u) => (
                    <div key={u.id} className="rounded-xl border border-ff-border p-5">
                      <div className="text-xs text-ff-medium">{new Date(u.created_at).toLocaleDateString()}</div>
                      <h4 className="mt-1 font-heading text-lg font-bold text-ff-blue">{u.title}</h4>
                      <p className="mt-2 whitespace-pre-line text-sm text-ff-dark">{u.body}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={MessageSquare} title="No updates yet" message="The organizer hasn't posted an update." testid="updates-empty" />
              )}
            </TabsContent>

            <TabsContent value="supporters" className="pt-6">
              {c.supporters?.length ? (
                <div className="space-y-3" data-testid="words-of-support">
                  {c.supporters.map((s, i) => (
                    <div key={i} className="rounded-xl border border-ff-border p-4" data-testid={`supporter-row-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ff-blue text-sm font-bold text-white">
                            {s.name?.[0]?.toUpperCase() || "?"}
                          </span>
                          <div>
                            <div className="text-sm font-semibold text-ff-blue">{s.name}</div>
                            {s.date && (
                              <div className="text-xs text-ff-medium">
                                {new Date(s.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                              </div>
                            )}
                          </div>
                        </div>
                        {s.amount != null && <div className="font-heading font-bold text-ff-blue">{money(s.amount)}</div>}
                      </div>
                      {s.message && (
                        <div className="mt-3 rounded-lg border-l-4 border-ff-green bg-[#F3F4F6] px-3 py-2" data-testid={`supporter-message-${i}`}>
                          <p className="text-sm italic leading-relaxed text-ff-dark">&ldquo;{s.message}&rdquo;</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Users} title="Be the first to donate" message="No public supporters yet — leave a message of support when you give." testid="supporters-empty" />
              )}
            </TabsContent>

            <TabsContent value="words" className="pt-6">
              <WordsOfSupport campaignId={c.id} defaultName={user?.name || ""} />
            </TabsContent>

            {c.is_team && (
              <TabsContent value="team" className="pt-6" data-testid="team-leaderboard">
                {c.team_members?.length ? (
                  <div className="space-y-3">
                    {c.team_members.map((m, i) => {
                      const pct = m.personal_goal ? Math.min(100, Math.round((m.amount_raised / m.personal_goal) * 100)) : 0;
                      return (
                        <div key={m.id} className="rounded-xl border border-ff-border p-4" data-testid={`team-member-${i}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-extrabold ${i === 0 ? "bg-ff-red text-white" : "bg-ff-blue text-white"}`}>
                                {i === 0 ? <Trophy className="h-4 w-4" /> : i + 1}
                              </span>
                              <div>
                                <div className="font-heading font-bold text-ff-blue">{m.name}</div>
                                {m.message && <div className="text-xs text-ff-medium">{m.message}</div>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-heading font-extrabold text-ff-blue">{money(m.amount_raised)}</div>
                              <div className="text-xs text-ff-medium">{m.donor_count || 0} donors</div>
                            </div>
                          </div>
                          {m.personal_goal ? (
                            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                              <div className="h-full rounded-full bg-ff-action" style={{ width: `${pct}%` }} />
                            </div>
                          ) : null}
                          <div className="mt-3 flex items-center gap-4">
                            <Link to={`/fundraisers/${c.slug}/team/${m.id}`} data-testid={`view-member-${m.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-ff-blue transition-colors duration-200 hover:text-ff-action">
                              <Users className="h-3.5 w-3.5" /> View {(m.name || "").split(" ")[0]}'s page
                            </Link>
                            <button data-testid={`share-member-${m.id}`}
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(`${window.location.origin}/api/s/${c.slug}?m=${m.id}`);
                                  toast.success(`${m.name}'s share link copied — the preview shows their own progress!`);
                                } catch { toast.error("Could not copy link"); }
                              }}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-ff-action transition-colors duration-200 hover:text-ff-blue">
                              <Share2 className="h-3.5 w-3.5" /> Share {(m.name || "").split(" ")[0]}'s page
                            </button>
                            <a href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(`${window.location.origin}/api/s/${c.slug}?m=${m.id}`)}`}
                              target="_blank" rel="noopener noreferrer" data-testid={`refresh-member-${m.id}`}
                              title="Refresh the social preview after progress updates"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-ff-medium transition-colors duration-200 hover:text-ff-red">
                              <RefreshCw className="h-3 w-3" /> Refresh preview
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState icon={Users} title="No team members yet" message="The organizer hasn't added members." testid="team-empty" />
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-4">
            <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
              <ProgressBar raised={c.amount_raised} goal={c.goal_amount} />
              {c.match && c.match.active && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-ff-green/30 bg-[#ECFDF5] p-3 text-xs" data-testid="match-banner">
                  <HandCoins className="mt-0.5 h-4 w-4 shrink-0 text-ff-green" />
                  <div>
                    <span className="font-bold text-ff-green">Donations matched by {c.match.sponsor_name}</span>
                    <div className="mt-0.5 text-ff-dark">
                      {money((c.match.pool_total || 0) - (c.match.matched_amount || 0))} left to be matched{c.match.multiplier > 1 ? ` (${c.match.multiplier}x)` : ""} — your gift goes further right now.
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ff-medium">
                <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {c.donor_count || 0} donor{c.donor_count !== 1 ? "s" : ""}</span>
                {c.donors_last_24h > 0 && (
                  <span className="flex items-center gap-1.5 font-semibold text-ff-red" data-testid="momentum-24h">
                    <Flame className="h-4 w-4" /> {c.donors_last_24h} in last 24h
                  </span>
                )}
              </div>
              {c.top_donor && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#F3F4F6] p-2.5 text-xs text-ff-dark" data-testid="momentum-top-donor">
                  <Trophy className="h-3.5 w-3.5 text-ff-action" /> Top donor: <span className="font-semibold text-ff-blue">{c.top_donor.name}</span> · {money(c.top_donor.amount)}
                </div>
              )}
              {c.can_donate ? (
                <Button asChild size="lg" data-testid="donate-button"
                  className="mt-5 w-full bg-ff-action text-base font-semibold text-white shadow-md hover:bg-ff-actionDark">
                  <Link to={`/fundraisers/${c.slug}/donate`}>
                    <Heart className="mr-2 h-4 w-4" /> Donate now
                  </Link>
                </Button>
              ) : (
                <div className="mt-5 flex items-center gap-2 rounded-lg border border-ff-border bg-[#F3F4F6] p-3 text-sm text-ff-dark" data-testid="not-accepting-notice">
                  <Clock className="h-4 w-4 text-ff-amber" /> This campaign is not accepting donations yet.
                </div>
              )}
              {c.can_donate && cryptoEnabled && (
                <div className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-[#ECFDF5] py-2 text-xs font-semibold text-ff-green" data-testid="crypto-accepted-badge">
                  <Bitcoin className="h-3.5 w-3.5 text-ff-green" /> Now accepting crypto &amp; cards
                </div>
              )}
              {c.can_donate && (
                <p className="mt-3 text-center text-[11px] leading-relaxed text-ff-medium" data-testid="detail-fee-disclosure">
                  5% platform fee (3% Pro) + Stripe processing fee on funds raised.
                </p>
              )}
              <button data-testid="report-campaign-link" onClick={() => navigate(`/report-campaign?campaign=${c.id}`)}
                className="mt-4 flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-ff-medium hover:text-ff-red">
                <Flag className="h-3.5 w-3.5" /> Report this campaign
              </button>
            </div>

            {/* Follow for updates */}
            <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="follow-card">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-heading text-sm font-bold text-ff-blue">Stay in the loop</div>
                  <p className="mt-0.5 text-xs text-ff-medium">
                    {followers > 0 ? `${followers} ${followers === 1 ? "person follows" : "people follow"} this campaign` : "Get an email when the organizer posts an update."}
                  </p>
                </div>
              </div>
              <button data-testid="follow-campaign-button" onClick={toggleFollow}
                className={`mt-3 flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${following ? "border-ff-green bg-[#ECFDF5] text-ff-green" : "border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white"}`}>
                {following ? <><Check className="h-4 w-4" /> Following</> : <><Bell className="h-4 w-4" /> Follow for updates</>}
              </button>
              {following && (
                <>
                  <PushToggle compact />
                  <InstallNudge
                    headline="Never miss an update"
                    sub="Add FightFund to your home screen so campaign updates are always one tap away."
                    testid="follow-install-nudge"
                  />
                </>
              )}
            </div>

            {/* Where your money goes — fee transparency */}
            <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="where-money-goes">
              <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
                <PiggyBank className="h-4 w-4 text-ff-green" /> Where your money goes
              </div>
              <p className="mt-1 text-xs text-ff-medium">
                On a $100 donation{c.organizer_pro ? " (Pro organizer)" : ""}:
              </p>
              <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-ff-border" aria-hidden="true">
                <div className="h-full bg-ff-green" style={{ width: `${100 - feePct}%` }} />
                <div className="h-full bg-ff-blue" style={{ width: `${feePct}%` }} />
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-ff-dark"><span className="h-2.5 w-2.5 rounded-full bg-ff-green" /> To the organizer</span>
                  <span className="font-bold text-ff-green" data-testid="wmg-organizer-amount">${100 - feePct}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-ff-dark"><span className="h-2.5 w-2.5 rounded-full bg-ff-blue" /> FightFund platform fee ({feePct}%)</span>
                  <span className="font-semibold text-ff-blue">${feePct}</span>
                </div>
                <div className="flex items-center justify-between text-ff-medium">
                  <span className="pl-[18px] text-xs">+ Stripe processing fee</span>
                  <span className="text-xs">applies</span>
                </div>
              </div>
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-[#ECFDF5] p-2.5 text-xs font-semibold text-ff-green">
                <BadgeCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Transparent by design — no hidden fees, ever.
              </p>
            </div>

            <ShareKit campaignId={c.id} slug={c.slug} title={c.title} crypto={cryptoEnabled} />
            <TextToGiveCard slug={c.slug} keyword={c.text_keyword} />
            <SupporterLeaderboard campaignId={c.id} />

            <TrustTimeline campaign={c} />

            <div className="rounded-2xl border border-ff-border bg-[#F3F4F6] p-5">
              <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
                <ShieldCheck className="h-4 w-4 text-ff-green" /> Reviewed before Payout
              </div>
              <p className="mt-2 text-xs leading-relaxed text-ff-dark">
                FightFund reviews campaigns and payout requests before funds are released. Donations are
                processed securely via Stripe.
              </p>
            </div>
          </div>
        </aside>
      </Container>

      {/* Mobile sticky donate bar — keeps the CTA in reach while reading */}
      {c.can_donate && (
        <>
          <div className="h-24 lg:hidden" aria-hidden="true" />
          <div data-testid="sticky-donate-bar"
            className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-ff-border bg-white/95 px-4 py-3 shadow-[0_-4px_20px_rgba(11,31,58,0.10)] backdrop-blur-xl lg:hidden">
            <div className="min-w-0">
              <div className="font-heading text-base font-extrabold leading-none text-ff-blue">{money(c.amount_raised)}</div>
              <div className="mt-1 truncate text-xs text-ff-medium">
                {c.goal_amount ? `of ${money(c.goal_amount)} goal` : "raised"} · {c.donor_count || 0} donor{c.donor_count !== 1 ? "s" : ""}
              </div>
            </div>
            <Button asChild size="lg" data-testid="sticky-donate-button"
              className="shrink-0 bg-ff-action px-6 font-semibold text-white shadow-md hover:bg-ff-actionDark">
              <Link to={`/fundraisers/${c.slug}/donate`}><Heart className="mr-2 h-4 w-4" /> Donate now</Link>
            </Button>
          </div>
        </>
      )}
      {/* Gallery lightbox */}
      {lightbox && (
        <div data-testid="gallery-lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <button data-testid="gallery-lightbox-close" onClick={() => setLightbox(null)}
            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-ff-blue hover:bg-white">
            <X className="h-5 w-5" />
          </button>
          <img src={lightbox} alt="Campaign photo" className="max-h-[85vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function TextToGiveCard({ slug, keyword }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/sms/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg?.enabled || !cfg?.number) return null;
  const code = (keyword || slug || "").toUpperCase();
  return (
    <div className="rounded-2xl border border-ff-border bg-white p-5" data-testid="text-to-give-card">
      <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
        <MessageSquare className="h-4 w-4 text-ff-blue" /> Give by text
      </div>
      <p className="mt-2 text-sm text-ff-dark">
        Text <span className="font-bold text-ff-blue">{code}</span> to{" "}
        <span className="font-bold text-ff-blue">{cfg.number}</span> and we'll text you a secure donation link.
      </p>
      <p className="mt-1.5 text-[11px] text-ff-medium">Msg &amp; data rates may apply. Reply STOP to opt out.</p>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-ff-border p-4">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-ff-medium" />
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-ff-medium">{label}</div>
        <div className="text-sm font-semibold text-ff-blue">{value}</div>
      </div>
    </div>
  );
}

function TrustTimeline({ campaign }) {
  const labels = campaign.trust_labels || [];
  const steps = [
    { key: "Campaign Reviewed", label: "Reviewed & approved", desc: "FightFund reviewed this campaign before public donations." },
    { key: "Organizer Verified", label: "Organizer verified", desc: "Identity & payout onboarding complete." },
    { key: "Nonprofit Verified", label: "Nonprofit verified", desc: "Verified U.S. 501(c)(3) organization." },
    { key: "Payout Under Review", label: "Payout under review", desc: "A payout request is being reviewed." },
    { key: "Funds Released", label: "Funds released", desc: "Funds released via Stripe rails." },
  ].filter((s) => labels.includes(s.key));
  if (!steps.length) return null;
  return (
    <div className="rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="trust-timeline">
      <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue">
        <ShieldCheck className="h-4 w-4 text-ff-green" /> Trust timeline
      </div>
      <ol className="mt-4 space-y-4">
        {steps.map((s, i) => (
          <li key={s.key} className="relative flex gap-3">
            <div className="flex flex-col items-center">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#ECFDF3]"><CheckCircle2 className="h-4 w-4 text-ff-green" /></span>
              {i < steps.length - 1 && <span className="mt-1 h-full w-0.5 flex-1 bg-ff-border" />}
            </div>
            <div className="pb-1">
              <div className="text-sm font-bold text-ff-blue">{s.label}</div>
              <div className="text-xs text-ff-medium">{s.desc}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
